import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Alert,
  Badge,
  Button,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Spinner,
} from "reactstrap";

import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_SERVICE_COMMISSION } from "../../../../helpers/helpCenter";
import {
  bulkApplyProfessionalServiceCommissions,
  copyProfessionalServiceCommissions,
  CopyServiceCommissionMode,
  getProfessionalServiceCommissionAudits,
  getProfessionalServiceCommissions,
  ProfessionalServiceCommissionAuditItem,
  ProfessionalServiceCommissionItem,
  syncProfessionalServiceCommissions,
  undoLastProfessionalServiceCommission,
} from "../../../../services/professionalServiceCommissionService";
import {
  getProfessionals,
  ProfessionalServiceCommissionOverride,
} from "../../../../services/professionalService";

interface ServiceCommissionPeer {
  id: number;
  name: string;
}

interface ProfessionalServiceCommissionPanelProps {
  professionalId: number;
  /** Default % from the form (may differ from last saved API value until save). */
  defaultCommissionPercent: string;
  /** When set (deep link from new service), pre-select this service in the add row. */
  preferredServiceId?: number | null;
  /** Called after a successful sync so the list badge can refresh. */
  onOverridesSaved?: (overrides: ProfessionalServiceCommissionOverride[]) => void;
}

const bpsToPercent = (bps: number): string => {
  const value = bps / 100;
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
};

const percentToBps = (percent: string): number | null => {
  const normalized = percent.replace(",", ".").trim();
  if (normalized === "") {
    return null;
  }
  const parsed = parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return Math.round(parsed * 100);
};

const formatPpDelta = (overrideBps: number, defaultBps: number): number | null => {
  const deltaBps = overrideBps - defaultBps;
  if (deltaBps === 0) {
    return null;
  }
  return deltaBps / 100;
};

interface DraftRow {
  service_id: number;
  service_name: string;
  percent: string;
  updated_at?: string | null;
}

const ProfessionalServiceCommissionPanel = ({
  professionalId,
  defaultCommissionPercent,
  preferredServiceId = null,
  onOverridesSaved,
}: ProfessionalServiceCommissionPanelProps) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [search, setSearch] = useState("");
  const [catalog, setCatalog] = useState<ProfessionalServiceCommissionItem[]>([]);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkPercent, setBulkPercent] = useState("");
  const [addServiceId, setAddServiceId] = useState("");
  const [addPercent, setAddPercent] = useState("");
  const [copySourceId, setCopySourceId] = useState("");
  const [copyMode, setCopyMode] = useState<CopyServiceCommissionMode>("replace");
  const [peerProfessionals, setPeerProfessionals] = useState<ServiceCommissionPeer[]>([]);
  const [audits, setAudits] = useState<ProfessionalServiceCommissionAuditItem[]>([]);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const defaultLabel =
    defaultCommissionPercent.trim() !== ""
      ? defaultCommissionPercent.trim()
      : "—";

  const defaultBps = useMemo(
    () => percentToBps(defaultCommissionPercent) ?? 0,
    [defaultCommissionPercent]
  );

  const formatSince = useCallback(
    (value: string | null | undefined): string | null => {
      if (!value) {
        return null;
      }

      return new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
        new Date(value)
      );
    },
    [i18n.language]
  );

  const formatWhen = useCallback(
    (value: string | null | undefined): string => {
      if (!value) {
        return "";
      }

      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language]
  );

  const mapOverridesForParent = (
    items: ProfessionalServiceCommissionItem[]
  ): ProfessionalServiceCommissionOverride[] =>
    items
      .filter((item) => item.is_override)
      .map((item) => ({
        service_id: item.service_id,
        service_name: item.service_name,
        commission_rate_bps: item.commission_rate_bps,
      }));

  const applyItems = useCallback(
    (items: ProfessionalServiceCommissionItem[]) => {
      setCatalog(items);
      setRows(
        items
          .filter((item) => item.is_override)
          .map((item) => ({
            service_id: item.service_id,
            service_name: item.service_name,
            percent: bpsToPercent(item.commission_rate_bps),
            updated_at: item.updated_at ?? null,
          }))
      );
      setSelectedIds((prev) =>
        prev.filter((id) => items.some((item) => item.is_override && item.service_id === id))
      );
    },
    []
  );

  const loadAudits = useCallback(async () => {
    try {
      const response = await getProfessionalServiceCommissionAudits(professionalId);
      setAudits(response.data?.items ?? []);
    } catch {
      setAudits([]);
    }
  }, [professionalId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProfessionalServiceCommissions(professionalId);
      const items = response.data?.items ?? [];
      applyItems(items);

      if (preferredServiceId) {
        const preferred = items.find(
          (item) => item.service_id === preferredServiceId && !item.is_override
        );
        if (preferred) {
          setAddServiceId(String(preferred.service_id));
        }
      }

      await loadAudits();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.serviceCommissions.toast.loadError")
      );
    } finally {
      setLoading(false);
    }
  }, [applyItems, loadAudits, preferredServiceId, professionalId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await getProfessionals({ per_page: 100 });
        if (cancelled) {
          return;
        }

        setPeerProfessionals(
          (response.data ?? [])
            .filter((peer) => peer.id !== professionalId && peer.is_active !== false)
            .map((peer) => ({ id: peer.id, name: peer.name }))
            .sort((a, b) => a.name.localeCompare(b.name, i18n.language))
        );
      } catch {
        if (!cancelled) {
          setPeerProfessionals([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, professionalId]);

  const availableToAdd = useMemo(() => {
    const overrideIds = new Set(rows.map((row) => row.service_id));
    return catalog.filter((item) => !overrideIds.has(item.service_id));
  }, [catalog, rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => row.service_name.toLowerCase().includes(term));
  }, [rows, search]);

  const toggleSelected = (serviceId: number) => {
    setSelectedIds((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const selectAllFiltered = () => {
    setSelectedIds(filteredRows.map((row) => row.service_id));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleAdd = () => {
    setFieldError(null);
    const serviceId = Number(addServiceId);
    if (!serviceId) {
      setFieldError(t("studio.professionals.serviceCommissions.validation.serviceRequired"));
      return;
    }
    const bps = percentToBps(addPercent);
    if (bps === null || bps < 0 || bps > 10000) {
      setFieldError(t("studio.professionals.serviceCommissions.validation.percentInvalid"));
      return;
    }
    const service = catalog.find((item) => item.service_id === serviceId);
    if (!service) {
      setFieldError(t("studio.professionals.serviceCommissions.validation.serviceRequired"));
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        service_id: service.service_id,
        service_name: service.service_name,
        percent: addPercent.replace(",", ".").trim(),
        updated_at: null,
      },
    ]);
    setAddServiceId("");
    setAddPercent("");
  };

  const handleRemove = async (serviceId: number) => {
    if (rows.length === 1) {
      const confirmation = await Swal.fire({
        title: t("studio.professionals.serviceCommissions.confirmRemoveLastTitle"),
        text: t("studio.professionals.serviceCommissions.confirmRemoveLastText", {
          percent: defaultLabel,
        }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("studio.professionals.serviceCommissions.confirmRemoveLastConfirm"),
        cancelButtonText: t("professionals.actions.cancel"),
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setRows((prev) => prev.filter((row) => row.service_id !== serviceId));
    setSelectedIds((prev) => prev.filter((id) => id !== serviceId));
  };

  const handleSave = async () => {
    setFieldError(null);
    const payload: { service_id: number; commission_rate_bps: number }[] = [];

    for (const row of rows) {
      const bps = percentToBps(row.percent);
      if (bps === null || bps < 0 || bps > 10000) {
        setFieldError(
          t("studio.professionals.serviceCommissions.validation.percentInvalidNamed", {
            name: row.service_name,
          })
        );
        return;
      }
      payload.push({ service_id: row.service_id, commission_rate_bps: bps });
    }

    setSaving(true);
    try {
      const response = await syncProfessionalServiceCommissions(professionalId, payload);
      toast.success(
        response.message || t("studio.professionals.serviceCommissions.toast.saveSuccess")
      );
      const items = response.data?.items ?? [];
      applyItems(items);
      onOverridesSaved?.(mapOverridesForParent(items));
      await loadAudits();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.serviceCommissions.toast.saveError")
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedServices = useMemo(() => {
    const byId = new Map(
      catalog.map((item) => [item.service_id, item.service_name] as const)
    );
    for (const row of rows) {
      byId.set(row.service_id, row.service_name);
    }
    return selectedIds
      .map((id) => ({ id, name: byId.get(id) ?? `#${id}` }))
      .filter((item) => item.name);
  }, [catalog, rows, selectedIds]);

  const handleUndoLast = async () => {
    if (audits.length === 0) {
      return;
    }

    const confirmation = await Swal.fire({
      title: t("studio.professionals.serviceCommissions.history.undoConfirmTitle"),
      text: t("studio.professionals.serviceCommissions.history.undoConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("studio.professionals.serviceCommissions.history.undoConfirm"),
      cancelButtonText: t("professionals.actions.cancel"),
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setUndoing(true);
    setFieldError(null);
    try {
      const response = await undoLastProfessionalServiceCommission(professionalId);
      toast.success(
        response.message || t("studio.professionals.serviceCommissions.history.undoSuccess")
      );
      const items = response.data?.items ?? [];
      applyItems(items);
      onOverridesSaved?.(mapOverridesForParent(items));
      await loadAudits();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.serviceCommissions.history.undoError")
      );
    } finally {
      setUndoing(false);
    }
  };

  const handleBulkApply = async () => {
    setFieldError(null);
    if (selectedIds.length === 0) {
      setFieldError(t("studio.professionals.serviceCommissions.bulk.noneSelected"));
      return;
    }
    const bps = percentToBps(bulkPercent);
    if (bps === null || bps < 0 || bps > 10000) {
      setFieldError(t("studio.professionals.serviceCommissions.validation.percentInvalid"));
      return;
    }

    setBulkApplying(true);
    try {
      const response = await bulkApplyProfessionalServiceCommissions(
        professionalId,
        selectedIds,
        bps
      );
      toast.success(
        response.message ||
          t("studio.professionals.serviceCommissions.bulk.toastSuccess", {
            count: response.data?.updated_count ?? selectedIds.length,
          })
      );
      const items = response.data?.items ?? [];
      applyItems(items);
      onOverridesSaved?.(mapOverridesForParent(items));
      setBulkPercent("");
      clearSelection();
      await loadAudits();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.serviceCommissions.bulk.toastError")
      );
    } finally {
      setBulkApplying(false);
    }
  };

  const handleCopy = async () => {
    const sourceId = Number(copySourceId);
    if (!sourceId) {
      setFieldError(t("studio.professionals.serviceCommissions.validation.copySourceRequired"));
      return;
    }

    const sourceName =
      peerProfessionals.find((peer) => peer.id === sourceId)?.name ?? "";

    const confirmation = await Swal.fire({
      title: t("studio.professionals.serviceCommissions.confirmCopyTitle"),
      text: t(
        copyMode === "merge"
          ? "studio.professionals.serviceCommissions.confirmCopyMergeText"
          : "studio.professionals.serviceCommissions.confirmCopyText",
        { name: sourceName }
      ),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("studio.professionals.serviceCommissions.confirmCopyConfirm"),
      cancelButtonText: t("professionals.actions.cancel"),
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setCopying(true);
    setFieldError(null);
    try {
      const response = await copyProfessionalServiceCommissions(
        professionalId,
        sourceId,
        copyMode
      );
      toast.success(
        response.message || t("studio.professionals.serviceCommissions.toast.copySuccess")
      );
      const items = response.data?.items ?? [];
      applyItems(items);
      onOverridesSaved?.(mapOverridesForParent(items));
      setCopySourceId("");
      await loadAudits();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.serviceCommissions.toast.copyError")
      );
    } finally {
      setCopying(false);
    }
  };

  const renderAuditLine = (audit: ProfessionalServiceCommissionAuditItem): string => {
    const who = audit.user_name || t("studio.professionals.serviceCommissions.history.someone");
    const from = audit.from_rate_bps != null ? bpsToPercent(audit.from_rate_bps) : "";
    const to = audit.to_rate_bps != null ? bpsToPercent(audit.to_rate_bps) : "";

    if (audit.action === "created") {
      return t("studio.professionals.serviceCommissions.history.created", {
        who,
        service: audit.service_name,
        to,
      });
    }
    if (audit.action === "deleted") {
      return t("studio.professionals.serviceCommissions.history.deleted", {
        who,
        service: audit.service_name,
        from,
      });
    }
    return t("studio.professionals.serviceCommissions.history.updated", {
      who,
      service: audit.service_name,
      from,
      to,
    });
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" className="me-2" />
        <span className="text-muted">{t("studio.professionals.serviceCommissions.loading")}</span>
      </div>
    );
  }

  return (
    <div id="professional-service-commissions" className="mt-4 pt-3 border-top">
      <h6 className="mb-1">{t("studio.professionals.serviceCommissions.title")}</h6>
      <p className="text-muted small mb-3">
        {t("studio.professionals.serviceCommissions.hint", { percent: defaultLabel })}
      </p>

      {preferredServiceId ? (
        <Alert color="info" className="mb-3">
          {t("studio.professionals.serviceCommissions.preferredServiceHint")}
        </Alert>
      ) : null}

      {rows.length === 0 ? (
        <Alert color="light" className="border mb-3">
          <p className="mb-2">
            {t("studio.professionals.serviceCommissions.empty", { percent: defaultLabel })}
          </p>
          <HelpArticleLink
            articlePath={HELP_ARTICLE_SERVICE_COMMISSION}
            label={t("studio.professionals.serviceCommissions.helpLink")}
          />
        </Alert>
      ) : (
        <>
          <Input
            type="search"
            className="mb-2"
            placeholder={t("studio.professionals.serviceCommissions.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <ul className="list-group list-group-flush mb-3">
            {filteredRows.map((row) => {
              const since = formatSince(row.updated_at);
              const rowBps = percentToBps(row.percent);
              const deltaPp =
                rowBps === null ? null : formatPpDelta(rowBps, defaultBps);
              const selected = selectedIds.includes(row.service_id);

              return (
                <li
                  key={row.service_id}
                  className="list-group-item px-0 d-flex flex-wrap align-items-center gap-2"
                >
                  <Input
                    type="checkbox"
                    className="mt-0"
                    checked={selected}
                    onChange={() => toggleSelected(row.service_id)}
                    aria-label={row.service_name}
                  />
                  <span className="flex-grow-1">
                    {row.service_name}
                    {deltaPp !== null ? (
                      <span className="d-block small text-muted">
                        {deltaPp > 0
                          ? t("studio.professionals.serviceCommissions.deltaPositive", {
                              pp: bpsToPercent(Math.round(deltaPp * 100)),
                            })
                          : t("studio.professionals.serviceCommissions.deltaNegative", {
                              pp: bpsToPercent(Math.round(Math.abs(deltaPp) * 100)),
                            })}
                      </span>
                    ) : null}
                    {since ? (
                      <span className="d-block small text-muted">
                        {t("studio.professionals.serviceCommissions.since", { date: since })}
                      </span>
                    ) : null}
                  </span>
                  <InputGroup style={{ maxWidth: 140 }}>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={row.percent}
                      onChange={(event) =>
                        setRows((prev) =>
                          prev.map((item) =>
                            item.service_id === row.service_id
                              ? { ...item, percent: event.target.value }
                              : item
                          )
                        )
                      }
                      aria-label={t("studio.professionals.serviceCommissions.percentAria", {
                        name: row.service_name,
                      })}
                    />
                    <InputGroupText>%</InputGroupText>
                  </InputGroup>
                  <Button
                    color="link"
                    className="text-danger p-0"
                    type="button"
                    onClick={() => void handleRemove(row.service_id)}
                  >
                    {t("studio.professionals.serviceCommissions.actions.remove")}
                  </Button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {catalog.length > 0 ? (
        <div className="border rounded p-3 mb-3 bg-light">
          <Label className="form-label small mb-2">
            {t("studio.professionals.serviceCommissions.bulk.title")}
          </Label>
          {rows.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button color="link" className="p-0" type="button" onClick={selectAllFiltered}>
                {t("studio.professionals.serviceCommissions.bulk.selectAll")}
              </Button>
              <Button color="link" className="p-0" type="button" onClick={clearSelection}>
                {t("studio.professionals.serviceCommissions.bulk.clear")}
              </Button>
            </div>
          ) : null}
          {availableToAdd.length > 0 ? (
            <div className="mb-2">
              <Input
                type="select"
                value=""
                onChange={(event) => {
                  const id = Number(event.target.value);
                  if (id) {
                    setSelectedIds((prev) =>
                      prev.includes(id) ? prev : [...prev, id]
                    );
                  }
                }}
              >
                <option value="">
                  {t("studio.professionals.serviceCommissions.fields.servicePlaceholder")}
                </option>
                {availableToAdd
                  .filter((item) => !selectedIds.includes(item.service_id))
                  .map((item) => (
                    <option key={item.service_id} value={item.service_id}>
                      {item.service_name}
                    </option>
                  ))}
              </Input>
              {selectedServices.length > 0 ? (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {selectedServices.map((service) => (
                    <Badge
                      key={service.id}
                      color="primary"
                      className="badge-soft-primary d-inline-flex align-items-center gap-1"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSelected(service.id)}
                      title={t("studio.professionals.serviceCommissions.bulk.removeChip")}
                    >
                      {service.name}
                      <span aria-hidden="true">×</span>
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          ) : selectedServices.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {selectedServices.map((service) => (
                <Badge
                  key={service.id}
                  color="primary"
                  className="badge-soft-primary d-inline-flex align-items-center gap-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSelected(service.id)}
                  title={t("studio.professionals.serviceCommissions.bulk.removeChip")}
                >
                  {service.name}
                  <span aria-hidden="true">×</span>
                </Badge>
              ))}
            </div>
          ) : null}
          <div className="d-flex flex-wrap align-items-end gap-2">
            <div style={{ width: 120 }}>
              <Label htmlFor="svc-commission-bulk-percent" className="form-label small mb-1">
                {t("studio.professionals.serviceCommissions.bulk.percent")}
              </Label>
              <InputGroup>
                <Input
                  id="svc-commission-bulk-percent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={bulkPercent}
                  onChange={(event) => setBulkPercent(event.target.value)}
                  placeholder="40"
                />
                <InputGroupText>%</InputGroupText>
              </InputGroup>
            </div>
            <Button
              color="soft-primary"
              type="button"
              disabled={bulkApplying || selectedIds.length === 0}
              onClick={() => void handleBulkApply()}
            >
              {bulkApplying
                ? t("studio.professionals.serviceCommissions.bulk.applying")
                : t("studio.professionals.serviceCommissions.bulk.apply")}
            </Button>
          </div>
        </div>
      ) : null}

      {availableToAdd.length > 0 ? (
        <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
          <div className="flex-grow-1" style={{ minWidth: 160 }}>
            <Label htmlFor="svc-commission-service" className="form-label small mb-1">
              {t("studio.professionals.serviceCommissions.fields.service")}
            </Label>
            <Input
              id="svc-commission-service"
              type="select"
              value={addServiceId}
              onChange={(event) => setAddServiceId(event.target.value)}
            >
              <option value="">
                {t("studio.professionals.serviceCommissions.fields.servicePlaceholder")}
              </option>
              {availableToAdd.map((item) => (
                <option key={item.service_id} value={item.service_id}>
                  {item.service_name}
                </option>
              ))}
            </Input>
          </div>
          <div style={{ width: 120 }}>
            <Label htmlFor="svc-commission-percent" className="form-label small mb-1">
              {t("studio.professionals.serviceCommissions.fields.percent")}
            </Label>
            <InputGroup>
              <Input
                id="svc-commission-percent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={addPercent}
                onChange={(event) => setAddPercent(event.target.value)}
                placeholder="40"
              />
              <InputGroupText>%</InputGroupText>
            </InputGroup>
          </div>
          <Button color="soft-primary" type="button" onClick={handleAdd}>
            {t("studio.professionals.serviceCommissions.actions.add")}
          </Button>
        </div>
      ) : null}

      {peerProfessionals.length > 0 ? (
        <div className="mb-3 mt-3 pt-3 border-top">
          <Label className="form-label small mb-2">
            {t("studio.professionals.serviceCommissions.fields.copyFrom")}
          </Label>
          <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
            <div className="flex-grow-1" style={{ minWidth: 180 }}>
              <Input
                id="svc-commission-copy"
                type="select"
                value={copySourceId}
                onChange={(event) => setCopySourceId(event.target.value)}
              >
                <option value="">
                  {t("studio.professionals.serviceCommissions.fields.copyFromPlaceholder")}
                </option>
                {peerProfessionals.map((peer) => (
                  <option key={peer.id} value={peer.id}>
                    {peer.name}
                  </option>
                ))}
              </Input>
            </div>
            <Button
              color="soft-secondary"
              type="button"
              disabled={copying || !copySourceId}
              onClick={() => void handleCopy()}
            >
              {copying
                ? t("studio.professionals.serviceCommissions.actions.copying")
                : t("studio.professionals.serviceCommissions.actions.copy")}
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-3">
            <div className="form-check mb-0">
              <Input
                id="svc-commission-copy-replace"
                type="radio"
                name="svc-commission-copy-mode"
                className="form-check-input"
                checked={copyMode === "replace"}
                onChange={() => setCopyMode("replace")}
              />
              <Label className="form-check-label small" htmlFor="svc-commission-copy-replace">
                {t("studio.professionals.serviceCommissions.copyMode.replace")}
              </Label>
            </div>
            <div className="form-check mb-0">
              <Input
                id="svc-commission-copy-merge"
                type="radio"
                name="svc-commission-copy-mode"
                className="form-check-input"
                checked={copyMode === "merge"}
                onChange={() => setCopyMode("merge")}
              />
              <Label className="form-check-label small" htmlFor="svc-commission-copy-merge">
                {t("studio.professionals.serviceCommissions.copyMode.merge")}
              </Label>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-3 pt-3 border-top">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h6 className="mb-0">{t("studio.professionals.serviceCommissions.history.title")}</h6>
          {audits.length > 0 ? (
            <Button
              color="soft-secondary"
              size="sm"
              type="button"
              disabled={undoing}
              onClick={() => void handleUndoLast()}
            >
              {undoing
                ? t("studio.professionals.serviceCommissions.history.undoing")
                : t("studio.professionals.serviceCommissions.history.undo")}
            </Button>
          ) : null}
        </div>
        {audits.length === 0 ? (
          <p className="text-muted small mb-0">
            {t("studio.professionals.serviceCommissions.history.empty")}
          </p>
        ) : (
          <ul className="list-unstyled mb-0">
            {audits.map((audit) => (
              <li key={audit.id} className="small mb-2">
                <span className="text-body">{renderAuditLine(audit)}</span>
                <span className="d-block text-muted">
                  {t("studio.professionals.serviceCommissions.history.when", {
                    date: formatWhen(audit.created_at),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {fieldError ? (
        <FormFeedback type="invalid" className="d-block mb-2 mt-2">
          {fieldError}
        </FormFeedback>
      ) : null}

      <div className="d-flex justify-content-end mt-3">
        <Button color="primary" type="button" disabled={saving} onClick={() => void handleSave()}>
          {saving
            ? t("studio.professionals.serviceCommissions.actions.saving")
            : t("studio.professionals.serviceCommissions.actions.save")}
        </Button>
      </div>
    </div>
  );
};

export default ProfessionalServiceCommissionPanel;
